import { CircularProgress, Container, Typography, Box, Card, CardContent, Divider, Alert } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import config from "../../utils/config";
import { toast } from "react-toastify";
import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useSearchParams } from "react-router-dom";

function PurchaseInfo() {
  const { organizationId, type } = useParams();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [statusText, setStatusText] = useState("Getting payment information...");
  const [paymentId, setPaymentId] = useState(null);

  useEffect(() => {
    // Stripe query params
    const sessionId = searchParams.get("sessionId");

    // Redsys query params
    const signature = searchParams.get("Ds_Signature");
    const signatureVer = searchParams.get("Ds_SignatureVersion");
    const merchantParameters = searchParams.get("Ds_MerchantParameters");

    if (sessionId != null) {
      getStripePaymentInfo(organizationId, sessionId);
    } else if (signature != null && signatureVer != null && merchantParameters != null) {
      getRedsysPaymentInfo(organizationId, signature, signatureVer, merchantParameters);
    }
  }, []); //eslint-disable-line

  const getStripePaymentInfo = (organizationId, sessionId) => {
    setTimeout(() => {
      axios
        .get(`${config.API_ENDPOINT}/checkout/session/${organizationId}/${sessionId}`)
        .then((res) => {
          const session = res.data;

          if (session.paymentStatus === "paid") {
            if (type === "esncard") {
              setStatusText("Thank you for purchasing your ESNcard. Please check your inbox.");
              setLoading(false);
              setPaymentId(session.paymentIntent);
            } else if (type === "event") {
              setStatusText("Thank you for purchasing this event. Please check your inbox.");
              setLoading(false);
              setPaymentId(session.paymentIntent);
            }
          } else {
            setStatusText("An error occurred while processing your payment.");
            setLoading(false);
            setPaymentId(session.paymentIntent);
          }
        })
        .catch(() => {
          toast.error("An unexpected error has occurred");
        });
    }, 2000);
  };

  const getRedsysPaymentInfo = (organizationId, signature, signatureVer, merchantParameters) => {
    setTimeout(() => {
      axios
        .post(`${config.API_ENDPOINT}/checkout/redsys/details`, {
          organization: organizationId,
          payload: {
            Ds_Signature: signature,
            Ds_SignatureVersion: signatureVer,
            Ds_MerchantParameters: merchantParameters,
          },
        })
        .then((res) => {
          const details = res.data;

          if (!details.isError) {
            if (type === "esncard") {
              setStatusText("Thank you for purchasing your ESNcard. Please check your inbox.");
              setLoading(false);
              setPaymentId(details.orderId);
            } else if (type === "event") {
              setStatusText("Thank you for purchasing this event. Please check your inbox.");
              setLoading(false);
              setPaymentId(details.orderId);
            }
          } else {
            setStatusText("An error occurred while processing your payment.");
            setLoading(false);
            setPaymentId(details.orderId);
          }
        })
        .catch(() => {
          toast.error("An unexpected error has occurred");
        });
    }, 2000);
  };

  const isSuccess = statusText.includes("Thank you");
  const isError = statusText.includes("error");

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Card sx={{ boxShadow: 3 }}>
        <CardContent sx={{ p: 4, textAlign: "center" }}>
          {loading ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <CircularProgress size={60} />
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                Processing your payment...
              </Typography>
            </Box>
          ) : (
            <Box>
              {isSuccess && (
                <>
                  <CheckCircleIcon sx={{ fontSize: 80, color: "#4caf50", mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#4caf50", mb: 1 }}>
                    Payment Successful!
                  </Typography>
                </>
              )}
              {isError && (
                <>
                  <ErrorIcon sx={{ fontSize: 80, color: "#f44336", mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#f44336", mb: 1 }}>
                    Payment Failed
                  </Typography>
                </>
              )}
              {!isSuccess && !isError && (
                <Box sx={{ mb: 2 }}>
                  <CircularProgress size={60} />
                </Box>
              )}

              <Typography variant="body1" sx={{ mb: 3, color: "text.primary" }}>
                {statusText}
              </Typography>

              {paymentId && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Alert severity="info">
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Payment Reference ID:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        backgroundColor: "#f5f5f5",
                        p: 1.5,
                        borderRadius: 1,
                        wordBreak: "break-all",
                        fontWeight: 700,
                        color: "#1976d2",
                      }}
                    >
                      {paymentId}
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "text.secondary" }}>
                      If you have any issues, contact your ESN office and provide this reference ID.
                    </Typography>
                  </Alert>
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}

export default PurchaseInfo;
